import { defaultPosterShape, POSTER_PAGE, POSTER_SHAPE_ASPECT, type PosterDoc, type PosterImageElement, type PosterStateResponse } from "@studio/shared";

/**
 * 처음 편집 화면에 들어왔을 때의 포스터 — **그린 그림만** 놓는다(2026-10-04 jery).
 *
 * 🔑 자리 글·뱃지·선 같은 샘플은 넣지 않는다. 학생이 지우는 데 시간을 쓰고, 안 지우면 남의 문구가 인쇄된다.
 * 🔑 그림은 자르지 않는다 — 그 그림의 모양(세로 3:4 · 가로 4:3 · 정사각) 비율 그대로 놓는다.
 *    첫 그림은 위에 크게, 나머지는 그 아래 한 줄로. 학생이 편집 화면에서 옮기고 키운다.
 * 🔑 «그리는 중» 인 칸도 넣는다 — 기다리는 동안 편집에 들어오면, 다 그려졌을 때 그 자리에 그림이 뜬다.
 * 🔑 QR 은 여기 없다 — 그리는 쪽이 구석에 자동으로 붙인다(autoQr.ts).
 */

const MARGIN = 20;
const GAP = 10;
/** 첫 그림이 차지할 수 있는 최대 높이 — 아래 줄을 위한 자리를 남긴다 */
const FIRST_MAX_H = 250;

type CutLike = Pick<PosterStateResponse["cuts"][number], "slot" | "image_url"> & Partial<Pick<PosterStateResponse["cuts"][number], "active_job" | "shape">>;

/** 칸 그림의 가로/세로 비율 — 그 그림의 모양. 모양을 모르면(옛 응답) 칸 기본 모양. */
export function cutAspect(cut: Pick<CutLike, "slot" | "shape">): number {
  return POSTER_SHAPE_ASPECT[cut.shape ?? defaultPosterShape(cut.slot)];
}

function image(id: string, cut: CutLike, x: number, y: number, w: number, h: number): PosterImageElement {
  return { id, type: "image", src: { kind: "slot", slot: cut.slot }, x, y, w, h, rot: 0, opacity: 1, shape: "rect", border: "none" };
}

export function starterDoc(state: { cuts: CutLike[] }): PosterDoc {
  const W = POSTER_PAGE.width;
  const drawn = state.cuts.filter((cut) => cut.image_url || cut.active_job).sort((left, right) => left.slot - right.slot);
  const els: PosterDoc["els"] = [];
  const [first, ...rest] = drawn;
  if (first) {
    const aspect = cutAspect(first);
    let w = W - MARGIN * 2;
    let h = w / aspect;
    if (h > FIRST_MAX_H || (rest.length === 0 && h > POSTER_PAGE.height - MARGIN * 2)) {
      h = rest.length === 0 ? Math.min(h, POSTER_PAGE.height - MARGIN * 2) : FIRST_MAX_H;
      w = h * aspect;
    }
    els.push(image(`img${first.slot}`, first, (W - w) / 2, MARGIN, w, h));
    if (rest.length > 0) {
      const top = MARGIN + h + GAP;
      const rowW = W - MARGIN * 2;
      const cellW = (rowW - GAP * (rest.length - 1)) / rest.length;
      const maxH = POSTER_PAGE.height - MARGIN - top;
      rest.forEach((cut, index) => {
        const aspect = cutAspect(cut);
        let cw = cellW;
        let ch = cw / aspect;
        if (ch > maxH) {
          ch = maxH;
          cw = ch * aspect;
        }
        const x = MARGIN + index * (cellW + GAP) + (cellW - cw) / 2;
        els.push(image(`img${cut.slot}`, cut, x, top, cw, ch));
      });
    }
  }
  return { version: 1, bg: "#fbf6ee", els };
}
